import * as React from 'react'

import { type NonFsdsTheme,resolveNonFsdsTheme } from '@/lib/non-fsds-theme'

interface NonFsdsStatsSectionProps {
  stats: Array<{ label: string; value: string }>
  theme?: NonFsdsTheme | string
}

const STAT_ICONS = [
  // Users/Learners
  <svg
    key="users"
    className="size-5"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.8}
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
    />
  </svg>,
  // Projects
  <svg
    key="projects"
    className="size-5"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.8}
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 0 0-1.883 2.542l.857 6a2.25 2.25 0 0 0 2.227 1.932H19.05a2.25 2.25 0 0 0 2.227-1.932l.857-6a2.25 2.25 0 0 0-1.883-2.542m-16.5 0V6A2.25 2.25 0 0 1 6 3.75h3.879a1.5 1.5 0 0 1 1.06.44l2.122 2.12a1.5 1.5 0 0 0 1.06.44H18A2.25 2.25 0 0 1 20.25 9v.776"
    />
  </svg>,
  // Mentors
  <svg
    key="mentors"
    className="size-5"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.8}
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5"
    />
  </svg>,
  // Completion rate
  <svg
    key="rate"
    className="size-5"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.8}
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z"
    />
  </svg>,
]

export default function NonFsdsStatsSection({ stats, theme = 'general' }: NonFsdsStatsSectionProps) {
  const themeStyles = {
    general: {
      divider: 'via-blue-200',
      cardBorder: 'border-blue-100/80',
      cardBg: 'from-blue-50/80',
      cardShadow: 'hover:shadow-blue-100/50',
      topGradient: 'from-blue-400 via-sky-400 to-blue-300',
      iconWrapper: 'bg-blue-100 text-blue-600',
      value: 'text-blue-700',
    },
    'english-academy': {
      divider: 'via-blue-200',
      cardBorder: 'border-blue-100/80',
      cardBg: 'from-blue-50/80',
      cardShadow: 'hover:shadow-emerald-100/50',
      topGradient: 'from-blue-400 via-emerald-400 to-blue-300',
      iconWrapper: 'bg-blue-100 text-blue-600',
      value: 'text-blue-700',
    },
    programming: {
      divider: 'via-indigo-200',
      cardBorder: 'border-indigo-100/80',
      cardBg: 'from-indigo-50/80',
      cardShadow: 'hover:shadow-indigo-100/50',
      topGradient: 'from-indigo-400 via-blue-400 to-indigo-300',
      iconWrapper: 'bg-indigo-100 text-indigo-600',
      value: 'text-indigo-700',
    },
    math: {
      divider: 'via-teal-200',
      cardBorder: 'border-teal-100/80',
      cardBg: 'from-teal-50/80',
      cardShadow: 'hover:shadow-teal-100/50',
      topGradient: 'from-teal-400 via-sky-400 to-teal-300',
      iconWrapper: 'bg-teal-100 text-teal-600',
      value: 'text-teal-700',
    },
  }[resolveNonFsdsTheme(theme)]

  return (
    <section data-testid="non-fsds-stats-section" className="relative bg-white px-4 py-14 sm:px-6 sm:py-16">
      <div className={`pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent to-transparent ${themeStyles.divider}`} />
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
          {stats.map((stat, index) => (
            <article
              key={stat.label}
              className={`group relative overflow-hidden rounded-2xl border bg-gradient-to-b to-white px-5 py-7 text-center shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${themeStyles.cardBorder} ${themeStyles.cardBg} ${themeStyles.cardShadow}`}
            >
              <div className={`pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r opacity-0 transition-opacity duration-300 group-hover:opacity-100 ${themeStyles.topGradient}`} />
              <div className={`mx-auto mb-3 flex size-10 items-center justify-center rounded-xl ${themeStyles.iconWrapper}`}>
                {STAT_ICONS[index % STAT_ICONS.length]}
              </div>
              <p className={`text-3xl font-bold tracking-tight sm:text-4xl ${themeStyles.value}`}>{stat.value}</p>
              <p className="mt-2 text-sm font-medium text-slate-600">{stat.label}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}
